// Primary colors (모드와 무관하게 공통으로 사용되는 색상)
export const primaryColors = {
    primary: {
        main: "#00dbff",
        light: "#00dbff",
        dark: "#00dbff",
    },
    primary2: {
        main: "#00AFCC",
        light: "#00AFCC",
        dark: "#00AFCC",
    },
    onPrimary: {
        main: "#ffffff",
        light: "#ffffff",
        dark: "#ffffff",
    },
    primaryVariant: {
        main: "#99F1FF",
        light: "#99F1FF",
        dark: "#99F1FF",
    },
    primaryVariant2: {
        main: "#00424D",
        light: "#00424D",
        dark: "#00424D",
    },
} as const;

// 각 모드별 색상 정의
export type ThemeMode = "light" | "dark";

interface ColorAssets {
    background: {
        default: string;
        bright: string;
        dim: string;
    };
    surface: {
        lowest: string;
        low: string;
        default: string;
        high: string;
        highest: string;
    };
    onSurface: {
        default: string;
        variant: string;
    };
    outline: {
        default: string;
        variant: string;
    };
    scrim: string;
}

// Light mode colors
const lightColors: ColorAssets = {
    background: {
        default: "#FAFAFA",
        bright: "#FFFFFF",
        dim: "#F2F2F2",
    },
    surface: {
        lowest: "#F8F9FA",
        low: "#EFF4F7",
        default: "#E9EFF1",
        high: "#E4E9EB",
        highest: "#DEE3E5",
    },
    onSurface: {
        default: "#171C1E",
        variant: "#3F484B",
    },
    outline: {
        default: "#70797B",
        variant: "#BFC8CB",
    },
    scrim: "rgba(33, 37, 41, 0.4)",
} as const;

// Dark mode colors
const darkColors: ColorAssets = {
    background: {
        default: "#111412",
        bright: "#373A37",
        dim: "#0B0D0C",
    },
    surface: {
        lowest: "#0C0F0D",
        low: "#191C1A",
        default: "#1D201E",
        high: "#272B28",
        highest: "#323633",
    },
    onSurface: {
        default: "#C5C7C3",
        variant: "#8A938C",
    },
    outline: {
        default: "#8A938C",
        variant: "#404943",
    },
    scrim: "rgba(33, 37, 41, 0.8)",
} as const;

// Theme configuration
const themeColors = {
    light: lightColors,
    dark: darkColors,
} as const;

// Theme hook과 함께 사용할 타입
export type ThemeColors = typeof lightColors;
export type PrimaryColors = typeof primaryColors;

// 현재 테마의 색상을 가져오는 함수
export const getThemeColors = (mode: ThemeMode): ColorAssets => {
    return themeColors[mode];
};

// 색상 사용을 위한 유틸리티 함수
export const createColorUtils = (mode: ThemeMode) => {
    const colors = getThemeColors(mode);

    return {
        ...primaryColors,
        ...colors,
        // 자주 사용되는 조합을 위한 유틸리티 함수들
        getBackgroundColor: (
            variant: keyof ColorAssets["background"] = "default"
        ) => colors.background[variant],
        getSurfaceColor: (variant: keyof ColorAssets["surface"] = "default") =>
            colors.surface[variant],
        getOnSurfaceColor: (
            variant: keyof ColorAssets["onSurface"] = "default"
        ) => colors.onSurface[variant],
        getOutlineColor: (variant: keyof ColorAssets["outline"] = "default") =>
            colors.outline[variant],
    };
};
